'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';

interface OrderFormProps {
  preselectedService?: string | null;
}

const services = [
  { id: 'air-freight', name: 'Air Freight Services', icon: 'PaperAirplaneIcon' },
  { id: 'ocean-freight', name: 'Ocean Freight', icon: 'public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png' },
  { id: 'ground-transport', name: 'Ground Transportation', icon: 'public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png' },
  { id: 'warehousing', name: 'Warehousing Solutions', icon: 'BuildingStorefrontIcon' },
  { id: 'customs-brokerage', name: 'Customs Brokerage', icon: 'DocumentCheckIcon' },
  { id: 'last-mile', name: 'Last Mile Delivery', icon: 'MapPinIcon' },
];

interface DestinationStop {
  id: string;
  location: string;
  city: string;
  country: string;
  stopType: 'pickup' | 'transit' | 'delivery';
}

export default function OrderForm({ preselectedService }: OrderFormProps) {
  const router = useRouter();
  const [formData, setFormData] = useState({
    serviceType: preselectedService || '',
    senderName: '',
    senderEmail: '',
    senderPhone: '',
    packageDescription: '',
    weight: '',
    dimensions: '',
    declaredValue: '',
  });

  const [destinations, setDestinations] = useState<DestinationStop[]>([
    { id: '1', location: '', city: '', country: '', stopType: 'pickup' },
    { id: '2', location: '', city: '', country: '', stopType: 'delivery' },
  ]);

  const [submitted, setSubmitted] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDestinationChange = (id: string, field: keyof DestinationStop, value: string) => {
    setDestinations(destinations.map(dest => 
      dest.id === id ? { ...dest, [field]: value } : dest
    ));
  };

  const addDestination = () => {
    const newId = (destinations.length + 1).toString();
    setDestinations([
      ...destinations,
      { id: newId, location: '', city: '', country: '', stopType: 'transit' },
    ]);
  };

  const removeDestination = (id: string) => {
    if (destinations.length > 2) {
      setDestinations(destinations.filter(dest => dest.id !== id));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Generate tracking number
    const newTrackingNumber = `TRK${Math.floor(100000000 + Math.random() * 900000000)}`;
    setTrackingNumber(newTrackingNumber);
    
    // Track conversion
    analytics.trackConversion('order_placed');
    analytics.trackUserAction('order_submitted', {
      service: formData.serviceType,
      trackingNumber: newTrackingNumber,
    });
    
    setSubmitted(true);
  };

  const handleTrackOrder = () => {
    router.push(`/track-shipment?tracking=${trackingNumber}`);
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card rounded-xl p-8 shadow-card border border-border text-center">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={48} className="text-success" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            Order Placed Successfully!
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            Your shipment has been registered and is being processed.
          </p>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
            <p className="text-sm text-muted-foreground mb-2">Your Tracking Number</p>
            <p className="text-2xl font-bold text-primary font-mono">{trackingNumber}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleTrackOrder}
              className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all flex items-center justify-center gap-2"
            >
              <Icon name="MagnifyingGlassIcon" size={20} />
              <span>Track Shipment</span>
            </button>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  serviceType: '',
                  senderName: '',
                  senderEmail: '',
                  senderPhone: '',
                  packageDescription: '',
                  weight: '',
                  dimensions: '',
                  declaredValue: '',
                });
                setDestinations([
                  { id: '1', location: '', city: '', country: '', stopType: 'pickup' },
                  { id: '2', location: '', city: '', country: '', stopType: 'delivery' },
                ]);
              }}
              className="px-8 py-3 bg-card text-foreground border border-border rounded-lg font-semibold hover:bg-muted transition-all"
            >
              Create Another Order
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Service Selection */}
        <div className="bg-card rounded-xl p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-6 flex items-center gap-2">
            <Icon name="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png" size={28} className="text-primary" />
            Select Service
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <label
                key={service.id}
                className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-primary/50 ${
                  formData.serviceType === service.id
                    ? 'border-primary bg-primary/5' :'border-border'
                }`}
              >
                <input
                  type="radio"
                  name="serviceType"
                  value={service.id}
                  checked={formData.serviceType === service.id}
                  onChange={handleChange}
                  className="sr-only"
                  required
                />
                <Icon name={service.icon as any} size={24} className="text-primary" />
                <span className="text-sm font-medium text-foreground">{service.name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Sender Information */}
        <div className="bg-card rounded-xl p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-6 flex items-center gap-2">
            <Icon name="UserIcon" size={28} className="text-primary" />
            Sender Information
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="senderName" className="block text-sm font-medium text-foreground mb-2">
                Full Name *
              </label>
              <input
                id="senderName"
                name="senderName"
                type="text"
                required
                value={formData.senderName}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label htmlFor="senderEmail" className="block text-sm font-medium text-foreground mb-2">
                Email Address *
              </label>
              <input
                id="senderEmail"
                name="senderEmail"
                type="email"
                required
                value={formData.senderEmail}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label htmlFor="senderPhone" className="block text-sm font-medium text-foreground mb-2">
                Phone Number *
              </label>
              <input
                id="senderPhone"
                name="senderPhone"
                type="tel"
                required
                value={formData.senderPhone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>
        </div>

        {/* Package Details */}
        <div className="bg-card rounded-xl p-8 shadow-card border border-border">
          <h2 className="text-2xl font-heading font-semibold text-foreground mb-6 flex items-center gap-2">
            <Icon name="CubeIcon" size={28} className="text-primary" />
            Package Details
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="packageDescription" className="block text-sm font-medium text-foreground mb-2">
                Package Description *
              </label>
              <textarea
                id="packageDescription"
                name="packageDescription"
                required
                value={formData.packageDescription}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="Describe the contents of your shipment"
              />
            </div>
            <div>
              <label htmlFor="weight" className="block text-sm font-medium text-foreground mb-2">
                Weight (kg) *
              </label>
              <input
                id="weight"
                name="weight"
                type="number"
                step="0.1"
                required
                value={formData.weight}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="10.5"
              />
            </div>
            <div>
              <label htmlFor="dimensions" className="block text-sm font-medium text-foreground mb-2">
                Dimensions (L×W×H cm) *
              </label>
              <input
                id="dimensions"
                name="dimensions"
                type="text"
                required
                value={formData.dimensions}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="50×40×30"
              />
            </div>
            <div>
              <label htmlFor="declaredValue" className="block text-sm font-medium text-foreground mb-2">
                Declared Value (USD) *
              </label>
              <input
                id="declaredValue"
                name="declaredValue"
                type="number"
                step="0.01"
                required
                value={formData.declaredValue}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                placeholder="500.00"
              />
            </div>
          </div>
        </div>

        {/* Destination Stops */}
        <div className="bg-card rounded-xl p-8 shadow-card border border-border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="MapPinIcon" size={28} className="text-primary" />
              Destination Stops
            </h2>
            <button
              type="button"
              onClick={addDestination}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/90 transition-all flex items-center gap-2"
            >
              <Icon name="PlusIcon" size={16} />
              <span>Add Stop</span>
            </button>
          </div>

          <div className="space-y-6">
            {destinations.map((dest, index) => (
              <div key={dest.id} className="relative border border-border rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                      dest.stopType === 'pickup' ? 'bg-emerald-500' :
                      dest.stopType === 'delivery' ? 'bg-blue-600' : 'bg-amber-500'
                    }`}>
                      {index + 1}
                    </div>
                    <select
                      value={dest.stopType}
                      onChange={(e) => handleDestinationChange(dest.id, 'stopType', e.target.value)}
                      className="px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground text-sm"
                    >
                      <option value="pickup">Pickup Point</option>
                      <option value="transit">Transit Stop</option>
                      <option value="delivery">Delivery Point</option>
                    </select>
                  </div>
                  {destinations.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeDestination(dest.id)}
                      className="text-error hover:text-error/80 transition-colors"
                    >
                      <Icon name="XMarkIcon" size={20} />
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Address *
                    </label>
                    <input
                      type="text"
                      required
                      value={dest.location}
                      onChange={(e) => handleDestinationChange(dest.id, 'location', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      required
                      value={dest.city}
                      onChange={(e) => handleDestinationChange(dest.id, 'city', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Country *
                    </label>
                    <input
                      type="text"
                      required
                      value={dest.country}
                      onChange={(e) => handleDestinationChange(dest.id, 'country', e.target.value)}
                      className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="United States"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            type="submit"
            className="px-12 py-4 bg-primary text-primary-foreground rounded-lg text-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 flex items-center gap-3 shadow-lg"
          >
            <Icon name="CheckCircleIcon" size={24} />
            <span>Place Order</span>
            <Icon name="ArrowRightIcon" size={24} />
          </button>
        </div>
      </form>
    </div>
  );
}